import { h } from '../lib/dom.js';
import { taskRow, openTaskModal, openBreakModal } from './task.js';
import { fmtDateRange, dayName, daysInWeek, todayIso } from '../lib/format.js';

/**
 * 7 günlük hafta planı — imza öğe.
 * Koç ve öğrenci panellerinde ortak.
 *
 * @param week            /api/weeks/{id} response (start_date, end_date, title, ...)
 * @param tasks           /api/weeks/{id}/tasks response.tasks
 * @param opts.canEdit    Koç → görev düzenle/arşivle; öğrenci → false
 * @param opts.onChange   Mutation callback (sayfayı yeniden render et)
 * @param opts.onAddTask  Koç için "+ Görev" butonu handler'ı (opsiyonel)
 */
export function renderWeekSpread(week, tasks, { canEdit = false, onChange, onAddTask } = {}) {
  const days = daysInWeek(week.start_date);
  const today = todayIso();
  const byDay = new Map(days.map(d => [d, []]));
  tasks.forEach(t => { if (byDay.has(t.task_date)) byDay.get(t.task_date).push(t); });

  const realTasks = tasks.filter(t => !Number(t.is_break));
  const breaks = tasks.filter(t => Number(t.is_break));
  const head = h('div', { class: 'week-spread-head' },
    h('div', {},
      h('div', { class: 'eyebrow' }, 'Haftalık plan'),
      h('h3', {}, week.title || fmtDateRange(week.start_date, week.end_date)),
    ),
    h('div', { class: 'row row-wrap' },
      h('span', { class: 'chip' }, realTasks.length + ' görev'),
      h('span', { class: 'chip' },
        h('span', { class: 'dot', style: { background: 'var(--status-completed)' } }),
        realTasks.filter(t => t.status === 'completed').length + ' tamamlandı',
      ),
      breaks.length ? h('span', { class: 'chip' }, '☕ ' + breaks.length + ' mola') : null,
    ),
  );

  const grid = h('div', { class: 'week-days' });
  days.forEach(d => {
    const isToday = d === today;
    const dayTasks = byDay.get(d) || [];
    const col = h('div', { class: 'week-day' + (isToday ? ' today' : '') });
    col.appendChild(h('div', { class: 'week-day-head' },
      h('div', {},
        h('div', { class: 'week-day-name' }, dayName(d, true)),
        h('div', { class: 'week-day-num' }, String(new Date(d).getDate())),
      ),
      h('span', { class: 'day-count' }, dayTasks.length || '·'),
    ));
    dayTasks.forEach(t => col.appendChild(taskRow(t, {
      canEdit, onChange, canComplete: true, compact: true,
    })));
    if (canEdit && onAddTask) {
      const suggestedStart = lastEndTime(dayTasks);
      const extras = suggestedStart ? { planned_start: suggestedStart } : {};
      col.appendChild(h('div', { class: 'day-add-actions', style: { marginTop: 'auto' } },
        h('button', {
          class: 'btn btn-ghost btn-sm',
          onclick: () => onAddTask(d, 'task', extras),
        }, '+ Görev'),
        h('button', {
          class: 'btn btn-ghost btn-sm',
          onclick: () => onAddTask(d, 'break', extras),
          title: 'Mola ekle',
        }, '+ Mola'),
      ));
    }
    grid.appendChild(col);
  });

  return h('div', { class: 'week-spread' }, head, grid);
}

/**
 * Bir günün görev/mola listesinden "en son biten" saati HH:MM olarak döner.
 * Sıralama: planned_end > planned_start + estimated_minutes > planned_start.
 * Hiçbiri yoksa null.
 */
function lastEndTime(dayTasks) {
  let maxMin = -1;
  for (const t of dayTasks) {
    let endMin = null;
    if (t.planned_end) {
      const [h, m] = t.planned_end.split(':').map(Number);
      endMin = h * 60 + m;
    } else if (t.planned_start && t.estimated_minutes) {
      const [h, m] = t.planned_start.split(':').map(Number);
      endMin = h * 60 + m + Number(t.estimated_minutes);
    } else if (t.planned_start) {
      const [h, m] = t.planned_start.split(':').map(Number);
      endMin = h * 60 + m;
    }
    if (endMin != null && endMin > maxMin) maxMin = endMin;
  }
  if (maxMin < 0) return null;
  const hh = String(Math.floor(maxMin / 60) % 24).padStart(2, '0');
  const mm = String(maxMin % 60).padStart(2, '0');
  return `${hh}:${mm}`;
}
